/** lag-paper-autocomplete
 *  
 *  EXAMPLE USAGE:
 *      <lag-paper-autocomplete
 *          auto-cache-key="some-unique-autosuggest-keyname-which-will-be-used-in-localstorage"
 *      ></lag-paper-autocomplete>
 */

import { PolymerElement } from '@polymer/polymer/polymer-element.js';
import '@LiveAuctionGroup/paper-autocomplete/paper-autocomplete.js';

import { defineElement } from '../../utility/defineElement.js';
import { log } from '../../app/log.js';

import { template } from './lag-paper-autocomplete.html.js';

const LAG_PAPER_AUTOCOMPLETE_STORAGE_KEY_PREFIX = "lag-paper-autocomplete:";

defineElement(class extends PolymerElement {

    static get is() { return 'lag-paper-autocomplete'; }

    static get template() { return template; }

    static get properties() {
        return {
            autoCacheKey: { type: String, observer: '_observeAutoCacheKey' },
            noAutoLoad: { type: Boolean, value: false },

            /** Inherited properties from paper-autocomplete, expose for outside use. **/
            label: { type: String },
            value: { type: String, notify: true },
            placeholder: { type: String },

            disabled: { type: Boolean },
            required: { type: Boolean },
            readonly: { type: Boolean },
            autocapitalize: { type: String },

            pattern: { type: String },
            allowedPattern: { type: String },
            maxLength: { type: Number },
            minLength: { type: Number },

            autoValidate: { type: Boolean },
            invalid: { type: Boolean, notify: true },
            errorMessage: { type: String },

            noLabelFloat: { type: Boolean },
            alwaysFloatLabel: { type: Boolean },
            disableShowClear: { type: Boolean },

            focused: { type: Boolean, notify: true },
            charCounter: { type: Boolean },

            autoClear: { type: Boolean, notify: true },
            clearDelay: { type: Number, notify: true },

            highlightFirst: { type: Boolean },
            showResultsOnFocus: { type: Boolean, value: true },
            queryFn: { type: Function },

            /** Properties of paper-autocomplete that we aren't using...
            remoteSource: { type: Boolean },
            eventNamespace: { type: String },
            name,
            value
            **/

            /** Private properties... **/
            _source: { type: Array, value: [] } // We'll deal with this internally (see code below).
        };
    }

    _observeAutoCacheKey(newVal, oldVal) {
        if (oldVal !== newVal && !!newVal) {
            this._loadSuggestions();
        }
    }

    ready() {
        // This is a hack to make sure the initial value is kept after super.ready(),
        // which seems to be clearing the value for some reason - not sure why.
        // Normally this wont matter since an element would initialize and then the
        // value would be set, however in some cases an element can be created unattached,
        // such as with document.createElement(), and then on someParent.append(ele) it
        // would clear the value.. so this hack resets the value afterwards :/
        const initialValue = this.value;
        super.ready();
        this.value = initialValue;

        if (!this.noAutoLoad) {
            this._loadSuggestions();
        }

        this._addEventHandlers();
    }

    validate() { return this.$.paperAutocomplete.validate(); }
    clear() { return this.$.paperAutocomplete.clear(); }
    hideSuggestions() { return this.$.paperAutocomplete.hideSuggestions(); }

    _getStorageKey() {
        if (!this.autoCacheKey || !this.autoCacheKey.length)
            throw new Error(`lag-paper-autocomplete#${this.id}.autoCacheKey invalid or missing`);

        return LAG_PAPER_AUTOCOMPLETE_STORAGE_KEY_PREFIX + this.autoCacheKey;
    }
    _getTrimmedVal() {
        return (this.$.paperAutocomplete.text || '').trim();
    }

    _loadSuggestions() {
        const suggestions = this._getCachedSuggestions();
        this.$.paperAutocomplete.set('source', suggestions);
    }
    _updateSuggestions(suggestions) {
        this.$.paperAutocomplete.set('source', suggestions); // Update UI component.
        this._setCachedSuggestions(suggestions); // Update cache.
    }

    _getCachedSuggestions() {
        try {
            const key = this._getStorageKey();

            const arrayOfSuggestionValues = JSON.parse(
                localStorage.getItem(key)
            ) || [];

            return arrayOfSuggestionValues.map(v => this._createSuggestionObjectFromValue(v));
        }
        catch (er) {
            log.error(er);
            return;
        }
    }
    _setCachedSuggestions(suggestions) {
        if (!suggestions || !(suggestions instanceof Array) || !suggestions.length)
            return;

        try {
            const key = this._getStorageKey();

            const arrayOfUniqueSuggestionValues = suggestions
                .map(v => this._extractSuggestionValueFromObject(v))
                .filter((v, idx, self) => self.indexOf(v) === idx); // Unique values.

            const serializedValues = JSON.stringify(arrayOfUniqueSuggestionValues);

            localStorage.setItem(key, serializedValues);
        }
        catch (er) {
            log.error(er);
        }
    }

    _createSuggestionObjectFromValue(val) {
        // Change values into text/value object as expected by underlying component.
        return {
            text: `${val}`,
            value: `${val}`
        };
    }
    _extractSuggestionValueFromObject(obj) {
        return obj && obj.value;
    }

    _addEventHandlers() {
        this.$.paperAutocomplete.addEventListener('autocomplete-blur', ev => this._handleBlur(ev));
        this.$.paperAutocomplete.addEventListener('autocomplete-reset-blur', ev => this._handleClear(ev));
    }

    _handleBlur(ev) {
        // Value has changed, add to suggestions if not already.

        const val = this._getTrimmedVal();
        if (!val.length)
            return;

        const suggestions = this.$.paperAutocomplete.source || [];
        const idx = suggestions.findIndex(v => v.value && v.value === val);

        if (idx >= 0) // Already exists.
            return;

        suggestions.push(this._createSuggestionObjectFromValue(val)); // Add new suggestion.
        this._updateSuggestions(suggestions);
    }

    _handleClear(ev) {
        // On clear button click remove the value that was cleared from suggestions if it exists (ie delete value).

        const val = ev && ev.detail && ev.detail.text;
        if (!val.length)
            return;

        const suggestions = this.$.paperAutocomplete.source || [];
        const idx = suggestions.findIndex(v => v.value && v.value === val);

        if (idx === -1) // Does not exist.
            return;

        suggestions.splice(idx, 1); // Remove the matched suggestion.
        this._updateSuggestions(suggestions);
    }
});